/**
 * Student Session Storage utility for managing student data during onboarding flow
 * 
 * Data is stored in localStorage and persists across page reloads until:
 * 1. Student is successfully created
 * 2. User logs out
 * 3. User manually clears the session
 */

export interface GradeOption {
  value: string;
  label: string;
}

export interface CampusData {
  id: string;
  name: string;
  location?: {
    lat?: number;
    lng?: number;
  };
  contact?: {
    address?: {
      primaryAddressLine?: string;
      secondaryAddressLine?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    };
  };
}

export interface ShiftData {
  id: string;
  name: string;
  displayLabel?: string;
  startTime?: string;
  endTime?: string;
}

export interface StudentSessionData {
  studentId?: string;
  formData: {
    givenName: string;
    familyName: string;
    gender: string;
    grade: GradeOption; // Full grade object { value, label }
    campus: CampusData; // Full campus object { id, name, location }
    shift: ShiftData; // Full shift object { id, name, startTime, endTime }
    geoAddress: string;
  };
  pickupLocation?: {
    coordinates: {
      lat: number;
      lng: number;
    };
    address: string;
  };
  startDate?: string;
  transportType?: string;
  tripType?: string;
  pricingConfig?: any;
  distanceInfo?: {
    distanceKm: number;
    durationMinutes: number;
    distanceText: string;
    durationText: string;
  };
  isNewStudent: boolean;
  createdAt?: number;
}

const SESSION_KEY = 'z_student_session_data';

/**
 * Save student session data to localStorage (persists across page reloads)
 */
export const saveStudentSession = (data: StudentSessionData): void => {
  try {
    const sessionData: StudentSessionData = {
      ...data,
      createdAt: data.createdAt || Date.now(),
    };
    localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
  } catch (error) {
    console.error('Error saving student session:', error);
  }
};

/**
 * Get student session data from localStorage
 */
export const getStudentSession = (): StudentSessionData | null => {
  try {
    const data = localStorage.getItem(SESSION_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error retrieving student session:', error);
    return null;
  }
};

/**
 * Update specific fields in student session
 */
export const updateStudentSession = (updates: Partial<StudentSessionData>): void => {
  try {
    const existing = getStudentSession();
    if (existing) {
      const updated = { ...existing, ...updates };
      saveStudentSession(updated);
    }
  } catch (error) {
    console.error('Error updating student session:', error);
  }
};

/**
 * Clear student session data from localStorage
 * Called after student is successfully created or user logs out
 */
export const clearStudentSession = (): void => {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch (error) {
    console.error('Error clearing student session:', error);
  }
};

/**
 * Check if session data exists and is still valid (not older than 24 hours)
 */
export const isSessionValid = (): boolean => {
  try {
    const data = getStudentSession();
    if (!data) return false;

    const now = Date.now();
    const sessionAge = now - (data.createdAt || 0);
    const twentyFourHours = 24 * 60 * 60 * 1000;

    return sessionAge < twentyFourHours;
  } catch (error) {
    console.error('Error checking session validity:', error);
    return false;
  }
};
